import { resetTextCaches } from '@vp/render';
import { DEFAULT_FONT_FAMILY, type ObjectRecord, readSpans } from '@vp/shared-types';

/**
 * Bundled fonts: one table serves the page (@font-face), the font picker and SVG export, where the
 * fonts in use are embedded so the file looks the same on any machine. System fonts cannot be
 * embedded (their files are not reachable from the page), so the default font is a bundled one.
 */
export interface FontOption {
  id: string;
  /** i18n key of the picker label. */
  label: string;
  /** CSS font-family stored in text styles. */
  family: string;
  /** Bundled family name (first in the stack); empty for the system stack. */
  name: string;
  /** Fontsource package folder. */
  pkg: string;
}

export const SYSTEM_FONT = 'system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const FONTS: FontOption[] = [
  { id: 'inter', label: 'fonts.inter', family: DEFAULT_FONT_FAMILY, name: 'Inter', pkg: 'inter' },
  { id: 'serif', label: 'fonts.serif', family: '"PT Serif", Georgia, serif', name: 'PT Serif', pkg: 'pt-serif' },
  { id: 'mono', label: 'fonts.mono', family: '"JetBrains Mono", Consolas, monospace', name: 'JetBrains Mono', pkg: 'jetbrains-mono' },
  { id: 'hand', label: 'fonts.hand', family: '"Caveat", cursive', name: 'Caveat', pkg: 'caveat' },
  { id: 'andika', label: 'fonts.andika', family: '"Andika", sans-serif', name: 'Andika', pkg: 'andika' },
  // OpenDyslexic has no Cyrillic: Cyrillic letters come from Andika, also made for easy reading
  { id: 'dyslexic', label: 'fonts.dyslexic', family: '"OpenDyslexic", "Andika", sans-serif', name: 'OpenDyslexic', pkg: 'opendyslexic' },
  { id: 'system', label: 'fonts.system', family: SYSTEM_FONT, name: '', pkg: '' },
];

const SUBSETS = {
  latin: 'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD',
  cyrillic: 'U+0301,U+0400-045F,U+0490-0491,U+04B0-04B1,U+2116',
} as const;
type Subset = keyof typeof SUBSETS;

interface Face {
  name: string;
  weight: 400 | 700;
  italic: boolean;
  subset: Subset;
  url: string;
  /** The same face as WOFF 1 (PDF export: fontkit cannot subset WOFF 2 reliably). */
  woff?: string;
}

const files = import.meta.glob('../../../node_modules/@fontsource/{inter,pt-serif,jetbrains-mono,caveat,andika,opendyslexic}/files/*-{latin,cyrillic}-{400,700}-{normal,italic}.woff2', {
  query: '?url',
  import: 'default',
  eager: true,
}) as Record<string, string>;

// WOFF 1 copies, only fetched when a PDF is made
const woffFiles = import.meta.glob('../../../node_modules/@fontsource/{inter,pt-serif,jetbrains-mono,caveat,andika,opendyslexic}/files/*-{latin,cyrillic}-{400,700}-{normal,italic}.woff', {
  query: '?url',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const FACES: Face[] = [];
for (const [path, url] of Object.entries(files)) {
  const m = /@fontsource\/([^/]+)\/files\/.+-(latin|cyrillic)-(400|700)-(normal|italic)\.woff2$/.exec(path);
  const font = m && FONTS.find((f) => f.pkg === m[1]);
  if (!m || !font) continue;
  FACES.push({ name: font.name, subset: m[2] as Subset, weight: Number(m[3]) as 400 | 700, italic: m[4] === 'italic', url, woff: woffFiles[path.slice(0, -1)] });
}

function faceCss(f: Face, src: string): string {
  return `@font-face{font-family:"${f.name}";font-style:${f.italic ? 'italic' : 'normal'};font-weight:${f.weight};font-display:swap;src:url(${src}) format("woff2");unicode-range:${SUBSETS[f.subset]};}`;
}

/** Fonts for which the font picker shows a choice (bundled ones and the system stack). */
export function fontOf(family: string | undefined): FontOption | undefined {
  const fam = family ?? DEFAULT_FONT_FAMILY;
  return FONTS.find((f) => f.family === fam) ?? FONTS.find((f) => f.name && fam.includes(`"${f.name}"`));
}

/**
 * Register the bundled faces with the page and redraw text when a face finishes loading (canvas
 * text drawn before that used a fallback and was measured with it).
 */
export function installFonts(onLoaded: () => void): void {
  if (typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.dataset.vp = 'fonts';
  style.textContent = FACES.map((f) => faceCss(f, f.url)).join('\n');
  document.head.appendChild(style);
  let timer = 0;
  document.fonts.addEventListener('loadingdone', () => {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      resetTextCaches();
      onLoaded();
    }, 30);
  });
  // the default font is needed right away; the others load when text uses them
  for (const w of ['400', '700']) void document.fonts.load(`${w} 16px Inter`, 'Aa Яя').catch(() => undefined);
}

/**
 * Load the faces the text of these objects needs before drawing them off screen (previews, PNG/SVG
 * export): fonts not shown on screen yet would otherwise be measured with a fallback.
 */
export async function ensureFontsFor(objects: Iterable<ObjectRecord>): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  const want = new Map<string, string>();
  for (const o of objects) {
    const st = o.style.text;
    const text = o.props['text'];
    if (!st || typeof text !== 'string' || !text) continue;
    const add = (bold: boolean | undefined, italic: boolean | undefined, family: string | undefined, sample: string) => {
      const spec = `${italic ? 'italic ' : ''}${bold ? 700 : 400} 16px ${family ?? DEFAULT_FONT_FAMILY}`;
      const prev = want.get(spec) ?? '';
      if (prev.length < 400) want.set(spec, prev + sample.slice(0, 200));
    };
    add(st.bold, st.italic, st.fontFamily, text);
    // formatted fragments: their own weight, slant and font
    for (const sp of readSpans(text, o.props['spans']) ?? []) if (sp.b || sp.i || sp.f) add(st.bold || sp.b, st.italic || sp.i, sp.f ?? st.fontFamily, sp.t);
  }
  let fresh = false;
  await Promise.all(
    [...want].map(async ([spec, text]) => {
      if (document.fonts.check(spec, text)) return;
      fresh = true;
      await document.fonts.load(spec, text).catch(() => undefined);
    }),
  );
  if (fresh) resetTextCaches();
}

// ---------- fonts for modules ----------

/** A face with embedded data, as the module runtime passes it (data: URL). */
export interface EmbeddedFace {
  family: string;
  weight: number;
  style: 'normal' | 'italic';
  unicodeRange?: string;
  url: string;
}

/** Platform families modules may use, with the CSS stack to put into primitives or views. */
export function platformFontsForModules(): { family: string; css: string }[] {
  return FONTS.filter((f) => f.name).map((f) => ({ family: f.name, css: f.family }));
}

/** Platform faces of the given families with their data embedded (for module workers and views). */
export async function platformFaces(names: string[]): Promise<EmbeddedFace[]> {
  const want = new Set(names);
  const faces = FACES.filter((f) => want.has(f.name));
  return Promise.all(
    faces.map(async (f) => ({ family: f.name, weight: f.weight, style: f.italic ? ('italic' as const) : ('normal' as const), unicodeRange: SUBSETS[f.subset], url: await toDataUrl(f.url).catch(() => f.url) })),
  );
}

/** Fonts that modules brought in their packages, by family: drawn on the canvas, embedded in SVG. */
const moduleFaces = new Map<string, EmbeddedFace[]>();

export function registerModuleFonts(_moduleId: string, faces: EmbeddedFace[]): void {
  if (typeof document === 'undefined' || !document.fonts) return;
  for (const f of faces) {
    const list = moduleFaces.get(f.family) ?? [];
    if (list.some((x) => x.weight === f.weight && x.style === f.style && x.unicodeRange === f.unicodeRange)) continue;
    list.push(f);
    moduleFaces.set(f.family, list);
    try {
      const face = new FontFace(f.family, 'url(' + f.url + ')', { weight: String(f.weight), style: f.style, ...(f.unicodeRange ? { unicodeRange: f.unicodeRange } : {}) });
      document.fonts.add(face);
      // canvas text does not request web fonts by itself: load now, text is redrawn when it arrives
      void face.load().catch(() => undefined);
    } catch {
      /* a broken font file: the fallback font is used */
    }
  }
}

/** Load the fonts named in primitives a module drew (canvas text does not ask for web fonts by itself). */
export function loadFamiliesOf(stacks: Iterable<string>): void {
  if (typeof document === 'undefined' || !document.fonts) return;
  const seen = new Set<string>();
  for (const stack of stacks) {
    if (seen.has(stack)) continue;
    seen.add(stack);
    for (const w of ['400', '700']) if (!document.fonts.check(w + ' 16px ' + stack, 'Aa Яя')) void document.fonts.load(w + ' 16px ' + stack, 'Aa Яя').catch(() => undefined);
  }
}

export interface FontUsage {
  /** CSS font-family as stored in the style. */
  family: string;
  bold: boolean;
  italic: boolean;
  text: string;
}

const dataUrls = new Map<string, Promise<string>>();
function toDataUrl(url: string): Promise<string> {
  let p = dataUrls.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) => r.blob())
      .then(
        (b) =>
          new Promise<string>((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(String(fr.result).replace(/^data:[^;]*;/, 'data:font/woff2;'));
            fr.onerror = () => reject(fr.error);
            fr.readAsDataURL(b);
          }),
      );
    dataUrls.set(url, p);
  }
  return p;
}

/**
 * @font-face rules with embedded data for the bundled fonts a drawing uses: only the weights,
 * styles and character subsets (Latin / Cyrillic) that actually occur.
 */
export async function embeddedFontCss(usage: FontUsage[]): Promise<string> {
  const want = new Set<Face>();
  for (const u of usage) {
    const names = FONTS.filter((f) => f.name && (u.family.includes(`"${f.name}"`) || u.family.startsWith(f.name))).map((f) => f.name);
    const subsets = new Set<Subset>();
    for (const ch of u.text) {
      const c = ch.codePointAt(0)!;
      if ((c >= 0x400 && c <= 0x45f) || c === 0x490 || c === 0x491 || c === 0x4b0 || c === 0x4b1 || c === 0x2116) subsets.add('cyrillic');
      else subsets.add('latin');
    }
    for (const name of names) {
      const weight = u.bold ? 700 : 400;
      for (const subset of subsets) {
        const face =
          FACES.find((f) => f.name === name && f.weight === weight && f.italic === u.italic && f.subset === subset) ??
          FACES.find((f) => f.name === name && f.weight === weight && !f.italic && f.subset === subset);
        if (face) want.add(face);
      }
    }
  }
  const rules = await Promise.all([...want].map(async (f) => faceCss(f, await toDataUrl(f.url).catch(() => f.url))));
  // fonts that modules brought: already embedded data
  const moduleRules: string[] = [];
  for (const [family, faces] of moduleFaces) {
    if (!usage.some((u) => u.family.includes('"' + family + '"') || u.family.startsWith(family))) continue;
    for (const f of faces) moduleRules.push(`@font-face{font-family:"${family}";font-style:${f.style};font-weight:${f.weight};src:url(${f.url});${f.unicodeRange ? 'unicode-range:' + f.unicodeRange + ';' : ''}}`);
  }
  return [...rules, ...moduleRules].join('\n');
}

// ---------- fonts for PDF ----------

/** Code point ranges of a CSS unicode-range ("U+0400-045F,U+2116"). */
function parseRange(range: string): [number, number][] {
  return range
    .split(',')
    .map((r) => r.trim().replace(/^U\+/i, ''))
    .filter(Boolean)
    .map((r) => {
      const [a, b] = r.split('-');
      const lo = parseInt(a.replace(/\?/g, '0'), 16);
      const hi = b ? parseInt(b, 16) : parseInt(a.replace(/\?/g, 'F'), 16);
      return [lo, hi] as [number, number];
    });
}
const covers = (ranges: [number, number][]) => (cp: number) => ranges.some(([lo, hi]) => cp >= lo && cp <= hi);

const fileBytes = new Map<string, Promise<Uint8Array>>();
function bytesOf(url: string): Promise<Uint8Array> {
  let p = fileBytes.get(url);
  if (!p) {
    p = fetch(url).then(async (r) => new Uint8Array(await r.arrayBuffer()));
    fileBytes.set(url, p);
  }
  return p;
}

/**
 * Font files for PDF export: the bundled (and module) families named in a CSS stack, in its order,
 * in the weight and style asked for (else the regular one), each file with the characters it has.
 */
export async function pdfFontFaces(family: string, bold: boolean, italic: boolean): Promise<{ bytes: Uint8Array; covers: (cp: number) => boolean }[]> {
  const names = family
    .split(',')
    .map((s) => s.trim().replace(/^["']|["']$/g, ''))
    .filter(Boolean);
  const out: { bytes: Uint8Array; covers: (cp: number) => boolean }[] = [];
  const weight = bold ? 700 : 400;
  for (const name of names) {
    const own = FACES.filter((f) => f.name === name);
    if (own.length) {
      for (const subset of Object.keys(SUBSETS) as Subset[]) {
        const face =
          own.find((f) => f.weight === weight && f.italic === italic && f.subset === subset) ??
          own.find((f) => f.weight === weight && !f.italic && f.subset === subset) ??
          own.find((f) => f.weight === 400 && !f.italic && f.subset === subset);
        if (face) out.push({ bytes: await bytesOf(face.woff ?? face.url), covers: covers(parseRange(SUBSETS[subset])) });
      }
      continue;
    }
    const mod = moduleFaces.get(name) ?? [];
    const pick = mod.filter((f) => f.weight === weight && f.style === (italic ? 'italic' : 'normal'));
    for (const f of pick.length ? pick : mod) out.push({ bytes: await bytesOf(f.url), covers: f.unicodeRange ? covers(parseRange(f.unicodeRange)) : () => true });
  }
  return out;
}
